import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, of } from 'rxjs';
import { catchError, concatMap, debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
    AccountsReceivableLedgerEntity,
    AccountsReceivableLedgerEntityFromBackend,
} from '../../entities/accounts-receivable-ledger.entity';
import { BusinessVolumeResolver } from '../../guards/business-volume.resolver';
import { LedgerImportResolver } from '../../guards/ledger-import.resolver';
import { ArlDataService } from '../../services/arl-data.service';
import { HttpService } from '../../services/http.service';
import { AccountsReceivableLedgerActionTypes, LedgerImportActionTypes } from '../actions/accounting.actions';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { EventsActionTypes } from '../actions/events.actions';
import { State } from '../index';
import { getAccountsReceivableLedgers } from '../selectors/accounting.selector';
import { getBusinessVolumeFetched } from '../selectors/business-volume.selector';
import { getToken } from '../selectors/base.selectors';
import { stringifyIfNotUndefined } from '../../../helper/helper';
import { FileEntity, FileEntityFromBackend } from '../../entities/file.entity';

enum ErrorCodes {
    Remove = 'Buchungsposition Löschen fehlgeschlagen',
    Modify = 'Buchungsposition ändern fehlgeschlagen',
    Add = 'Buchungsposition hinzufügen fehlgeschlagen',
    Storno = 'Buchungsposition(en) stornieren fehlgeschlagen',
    Export = 'Buchungen Exportieren fehlgeschlagen',
}

@Injectable()
export class AccountsReceivableLedgerEffects {
    constructor(
        private actions$: Actions<DaveActions>,
        private store$: Store<State>,
        private gatewayHttpService: HttpService,
        private bvResolver: BusinessVolumeResolver,
        private liResolver: LedgerImportResolver,
        private arlDataService: ArlDataService,
    ) {}

    PollFolders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EventsActionTypes.AddEvent, EventsActionTypes.ModifyEvent),
            debounceTime(2000),
            tap(() => this.arlDataService.getAllArls()),
        ),
        {dispatch: false},
    );

    GetAllAccountsReceivableLedgerQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountsReceivableLedgerActionTypes.GetAccountsReceivableLedger),
            exhaustMap(({Payload}) => {
                return this.arlDataService.getARLs(Payload).then((res) => {
                    if (res && !(Payload?.updatedSince && !res.length)) {
                        this.store$.dispatch(
                            AccountsReceivableLedgerActionTypes.UpdateAccountsReceivableLedger({
                                Payload: res,
                                updateLatestUpdatedAt: true,
                            }),

                        );
                    }
                });
            }),
        ),
        {dispatch: false},
    );

    PollAccountsReceivableLedger$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountsReceivableLedgerActionTypes.PollAccountsReceivableLedger),
            tap(() => {
                this.arlDataService.getAllArls();
            }),
        ),
        {dispatch: false},
    );

    AddAccountsReceivableLedger$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AccountsReceivableLedgerActionTypes.AddAccountsReceivableLedger),
                tap(({ Payload }) => {
                    this.arlDataService.addArls([Payload], 100);
                }),
            ),
        { dispatch: false },
    );

    ChangeAccountsReceivableLedger$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AccountsReceivableLedgerActionTypes.ChangeAccountsReceivableLedger),
                tap(({ Payload }) => {
                    this.arlDataService.changeArls([Payload], 100);
                }),
            ),
        { dispatch: false },
    );


    StornoAccountsReceivableLedger$ = createEffect(() => this.actions$.pipe(
        ofType(AccountsReceivableLedgerActionTypes.StornoAccountsReceivableLedger),
        switchMap(({ Payload }) => {
            const queryString = `mutation {
                stornoAccountsReceivableLedger(ids: ${JSON.stringify(Payload.ids)})
                {${AccountsReceivableLedgerEntity.GqlFields}}
        }
        `;
            return this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                withLatestFrom(this.store$.select(getAccountsReceivableLedgers)),
                map(([res, bookingPositions]) =>
                    res && res.stornoAccountsReceivableLedger
                        ? AccountsReceivableLedgerActionTypes.UpdateAccountsReceivableLedger({
                              Payload: [...bookingPositions.filter(b => !res.stornoAccountsReceivableLedger.some(a => a.id === b.Id)), ...res.stornoAccountsReceivableLedger.map(v => AccountsReceivableLedgerEntityFromBackend(v))],
                          })
                        : BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Storno } }),
                ),
                catchError((err, caught) =>
                    of(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Storno,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    ),
                ),
            );
        }),
    ));


    DeleteAccountsReceivableLedger$ = createEffect(() => this.actions$.pipe(
        ofType(AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedger),
        concatMap(({ Payload }) => {
            const queryString = `mutation {
          deleteAccountsReceivableLedger(
    id: ${Payload.id}
          )
        }
        `;
                return this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                    withLatestFrom(this.store$.select(getAccountsReceivableLedgers), this.store$.select(getBusinessVolumeFetched)),
                    tap(([, , bvFetched]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                    }),
                    concatMap(([res, bookingPositions]) => {
                        return res && res.deleteAccountsReceivableLedger
                            ? [
                                  LedgerImportActionTypes.GetLedgerImports({ Payload: {} }),
                                  AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedgerSuccess({
                                      Payload: Payload.id,
                                  }),
                              ]
                            : [
                                  AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedgerFailure({
                                      Payload: {
                                          Errors: 'Wrong response deleteAccountsReceivableLedger',
                                          ToasterMessage: ErrorCodes.Remove,
                                      },
                                  }),
                              ];
                    }),
                    catchError((err, caught) =>
                        of(
                            AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedgerFailure({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Remove,
                                    Errors: err,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );


    createCsv$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountsReceivableLedgerActionTypes.ExportRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const params = [
                    stringifyIfNotUndefined(Payload, 'status'),
                    stringifyIfNotUndefined(Payload, 'contractGroupTypeId'),
                    stringifyIfNotUndefined(Payload, 'mandateTypeId'),
                    stringifyIfNotUndefined(Payload, 'treatmentStartdate'),
                    stringifyIfNotUndefined(Payload, 'treatmentEnddate'),
                    stringifyIfNotUndefined(Payload, 'dTAStartdate'),
                    stringifyIfNotUndefined(Payload, 'dTAEnddate')
                ].filter(p => p !== '').join('\n    ');
                const queryString = `
mutation{
  createArlCSV${params ? `(\n    ${params}\n  )` : ''} {
        ${FileEntity.GqlFields}
  }
}
`
                return this.gatewayHttpService.graphQl({ query: queryString }, { token , retry: true }).pipe(
                    concatMap((res) =>
                    {
                        return   res && res.createArlCSV && res.createArlCSV.id
                            ? [AccountsReceivableLedgerActionTypes.ExportSuccess({Payload: FileEntityFromBackend(res.createArlCSV)})]
                            : [AccountsReceivableLedgerActionTypes.ExportFailure({ ToasterMessage: ErrorCodes.Export, Errors: 'createArlCsv wrong response' })]
                    }),
                    catchError((err) => of(AccountsReceivableLedgerActionTypes.ExportFailure({ ToasterMessage: ErrorCodes.Export, Errors: err }))),
                );
            }),
        ),
    );
    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedgerFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
}
